/*
 * File: SocialAccountCard.tsx
 * Project: a-a
 * File Created: Tuesday, 17th June 2025 10:00:08 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Tuesday, 9th September 2025 8:29:58 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import React from 'react';
import zeplinColor from '../../../../../theme/Color';
import { LightTooltip } from '../../../../shared/CustomTooltip/CustomTooltip';
import CustomPopOver from '../../../../shared/CustomPopOver/CustomPopOver';
import { Link } from 'react-router-dom';

// Utility function to format numbers
const formatNumber = (num: number): string => {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(2)}B`; // Billion
  } else if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(2)}M`; // Million
  } else if (num >= 1_000) {
    return `${(num / 1_000).toFixed(2)}k`; // Thousand
  }
  return num.toString(); // Less than 1,000
};

const SocialAccountCard = ({
  loading,
  followers,
  username,
  platform,
  url,
  verified,
}: {
  loading: boolean;
  followers: number;
  username: string;
  platform: string;
  url: string;
  verified: boolean;
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLImageElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.src = '/assets/captive8.svg'; // Path to fallback image
  };
  return (
    <Link
      // to='https://captiv8.io/insights/signin'
      to={url}
      style={{
        color: zeplinColor.BasicBlack,
        textDecoration: 'none',
        // position: 'absolute',
        // top: 0,
        // right: 0,
        // zIndex: 1000,
      }}
      target='_blank'
      rel='noopener noreferrer'
    >
      <Box
        sx={{
          borderRadius: '16px',
          padding: { xs: '8px 16px', xl: '16px' },
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          backgroundColor: zeplinColor.Neutral3,
          // width: '172px',
          // height: '117px',
          ...(username && { '&:hover': { border: `1px solid ${zeplinColor.BasicBlack}` } }),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {loading ? (
            <Skeleton variant='rounded' width={24} height={24} />
          ) : (
            <img
              src={`/assets/${platform?.toLocaleLowerCase()}-captive8.svg`}
              onError={handleImageError}
              alt='social-engagement-icon'
              id='social-engagement-icon'
              width={24}
              height={24}
            />
          )}
          {/* <LightTooltip
          title={
            <Typography variant='body3' color={zeplinColor.Neutral1}>
              Options
            </Typography>
          }
          placement='bottom-end'
          style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
        >
          
          <img
            width={'18px'}
            height={'18px'}
            src={'/assets/vertical-dots.svg'}
            alt='options-icon'
            onClick={handleClick}
            aria-hidden='false'
            // style={{ imageRendering: 'pixelated', filter: 'blur(0px)' }}
          />
        </LightTooltip> */}
        </Box>
        {loading ? (
          <Skeleton
            variant='text'
            sx={{
              fontSize: theme.typography.body2,
              width: '100%',
              // Optionally adjust other styles here
            }}
          />
        ) : (
          <Typography variant='display6'>{formatNumber(followers)}</Typography>
        )}
        {loading ? (
          <Skeleton
            variant='text'
            sx={{
              fontSize: theme.typography.body2,
              width: '100%',
              // Optionally adjust other styles here
            }}
          />
        ) : (
          <Box sx={{ display: 'flex', gap: '4px' }}>
            <Typography variant='body3'>{username}</Typography>
            {verified && (
              <img
                width={'16px'}
                height={'16px'}
                src={'/assets/verified.svg'}
                alt='options-icon'
                onClick={handleClick}
                aria-hidden='false'
              />
            )}
          </Box>
        )}
        <CustomPopOver
          setAnchorEl={setAnchorEl}
          anchorEl={anchorEl}
          id={id}
          sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              padding: '16px',
            }}
          >
            {/* <a
            href={#}
            target='_blank'
            rel='noopener noreferrer'
            style={{ textDecoration: 'none' }}
          > */}
            <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}>
              <img src={'/assets/view.svg'} width={18} height={18} />
              <Typography variant='body3' color={zeplinColor.BasicBlack}>
                View details
              </Typography>
            </Box>
            {/* </a> */}
            <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={() => {}}>
              <img src={'/assets/edit.svg'} width={18} height={18} />
              <Typography variant='body3' color={zeplinColor.BasicBlack}>
                Edit
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={() => {}}>
              <img src={'/assets/copy-link.svg'} width={18} height={18} />
              <Typography variant='body3' color={zeplinColor.BasicBlack}>
                Copy Link
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={(e) => {}}>
              <img src={'/assets/delete-red.png'} width={18} height={18} />
              <Typography variant='body3' color={zeplinColor.Error}>
                Delete
              </Typography>
            </Box>
          </Box>
        </CustomPopOver>
      </Box>
    </Link>
  );
};

export default SocialAccountCard;
